import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllDesigns,
  updateDesign,
  updateDesignVersion,
  getAllRequirements,
  getAllEngagements,
  createLedgerEntry,
  createAuditLog,
} from '../../store/actions';
import { CheckCircle, RefreshCw, Lock, AlertTriangle, ChevronLeft, Eye, XCircle } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { DESIGN_STATUS, DESIGN_VERSION_STATUS, GARMENT_TYPE, formatINR, label, options, today } from '../../constants/garmentCodes';

const STATUS = { AWAITING_CHARGE: 1, IN_PROGRESS: 2, SUBMITTED: 3, REVISION: 4, APPROVED: 5, FINALISED: 6 };
const CHARGE = { PENDING: 1, APPROVED: 2, REJECTED: 3 };
const VERSION = { PENDING: 1, REVISION: 2, APPROVED: 3 };
const FINALISED = 4;
const REVISION_REASONS = ['Fit issue', 'Colourway issue', 'Placement issue', 'Construction issue', 'Silhouette issue', 'Measurement issue', 'Brand direction changed'];
const textareaCls = 'w-full mt-1.5 px-3 py-2 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0] resize-none';

const latest = (design) => {
  const versions = design?.versions || [];
  return versions.length ? versions[versions.length - 1] : null;
};

export default function BrandDesigns() {
  const dispatch = useDispatch();
  const designs = useSelector((state) => state.design.designs);
  const loading = useSelector((state) => state.design.loading);
  const requirements = useSelector((state) => state.requirement.requirements);
  const engagements = useSelector((state) => state.engagement.engagements);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const companyId = useSelector((state) => state.auth?.companyId);
  const brandId = useSelector((state) => state.brand.profile?.id);

  const [viewer, setViewer] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [chargeTarget, setChargeTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [finaliseTarget, setFinaliseTarget] = useState(null);
  const [revisionTarget, setRevisionTarget] = useState(null);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    dispatch(getAllRequirements({ brandId }));
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllDesigns({ brandId, status: '' }));
  }, [dispatch, brandId]);

  const engagementOf = useMemo(() => {
    const engById = Object.fromEntries(engagements.map((e) => [String(e.id), e]));
    const reqById = Object.fromEntries(requirements.map((r) => [String(r.id), r]));
    return (design) => engById[String(reqById[String(design?.requirementId)]?.engagementId)] || null;
  }, [engagements, requirements]);

  const openModal = (setter, design) => {
    setNote('');
    setReason('');
    setConfirmed(false);
    setViewer(null);
    setter(design);
  };

  const audit = (design, action, summary) =>
    dispatch(createAuditLog({ payload: { companyId, userId, action, entityType: 'Design', entityId: design.id, engagementId: engagementOf(design)?.id ?? null, summary } }));

  const approveCharge = () => {
    const design = chargeTarget;
    const engagement = engagementOf(design);
    dispatch(updateDesign({ payload: { id: design.id, chargeStatus: CHARGE.APPROVED, status: STATUS.IN_PROGRESS, stage: 6, finalisationStatus: 2 } }));
    if (engagement && design.charge) {
      dispatch(
        createLedgerEntry({
          payload: {
            engagementId: engagement.id,
            type: 4,
            category: 3,
            date: today(),
            postingDate: today(),
            description: `Design charge hold — ${design.name}`,
            held: design.charge,
            status: 2,
            approvalStatus: 1,
            postedByUserId: userId,
            linkedItemType: 'Design',
            linkedItemId: design.id,
          },
        }),
      );
    }
    if (note.trim()) audit(design, 'Design charge approved', note.trim());
    setChargeTarget(null);
  };

  const rejectCharge = () => {
    dispatch(updateDesign({ payload: { id: chargeTarget.id, chargeStatus: CHARGE.REJECTED } }));
    audit(chargeTarget, 'Design charge rejected', note.trim() || 'Charge rejected');
    setChargeTarget(null);
  };

  const approveVersion = () => {
    const design = approveTarget;
    const version = latest(design);
    if (version?.id) dispatch(updateDesignVersion({ payload: { id: version.id, status: VERSION.APPROVED } }));
    dispatch(updateDesign({ payload: { id: design.id, status: STATUS.APPROVED, stage: 7 } }));
    if (note.trim()) audit(design, 'Design version approved', note.trim());
    setApproveTarget(null);
  };

  const requestRevision = () => {
    const design = revisionTarget;
    const version = latest(design);
    if (version?.id) dispatch(updateDesignVersion({ payload: { id: version.id, status: VERSION.REVISION } }));
    dispatch(updateDesign({ payload: { id: design.id, status: STATUS.REVISION, stage: 6, finalisationStatus: 2 } }));
    audit(design, 'Design revision requested', [reason, note.trim()].filter(Boolean).join(' — '));
    setRevisionTarget(null);
  };

  const finalise = () => {
    const design = finaliseTarget;
    dispatch(updateDesign({ payload: { id: design.id, status: STATUS.FINALISED, finalisationStatus: FINALISED, stage: 8 } }));
    audit(design, 'Design finalised', note.trim() || `Finalised V${design.currentVersion}`);
    setFinaliseTarget(null);
  };

  const reviewable = (d) => d.status === STATUS.SUBMITTED && d.finalisationStatus !== FINALISED;
  const chargeEngagement = engagementOf(chargeTarget);
  const viewerVersion = viewer?.versions?.[viewerIndex];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Designs" subtitle={`${designs.length} design requests`} />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="flex items-center gap-3">

          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        {!loading && designs.length === 0 && <div className="bg-white rounded-xl border border-[#E5E3DE] py-12 text-center text-sm text-[#9B9893]">No design requests</div>}

        {designs.map((design) => {
          const status = label(DESIGN_STATUS, design.status);
          const versions = design.versions || [];
          const pct = design.revisionAllowance ? Math.min(100, (design.revisionsUsed / design.revisionAllowance) * 100) : 0;
          return (
            <div key={design.id} className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden hover:shadow-sm transition-shadow">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>{design.name}</h3>
                      <Badge variant={statusToBadge(status)}>{status}</Badge>
                      {reviewable(design) && <Badge variant="purple">Ready for review</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-[#9B9893]">
                      <span>{design.requirementName}</span>
                      <span>·</span>
                      <span>{label(GARMENT_TYPE, design.garmentType)}</span>
                      <span>·</span>
                      <span>{design.manufacturerName}</span>
                      <span>·</span>
                      <span>Charge: {formatINR(design.charge)}</span>
                      {design.currentVersion > 0 && (
                        <>
                          <span>·</span>
                          <span>V{design.currentVersion} · {design.revisionsUsed ?? 0}/{design.revisionAllowance ?? 0} revisions</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {design.chargeStatus === CHARGE.PENDING && <Button variant="primary" size="sm" onClick={() => openModal(setChargeTarget, design)}>Review charge</Button>}
                    {versions.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => { setViewerIndex(versions.length - 1); setViewer(design); }}>
                        <Eye size={13} className="mr-1.5" />View
                      </Button>
                    )}
                  </div>
                </div>

                {design.currentVersion > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-[#9B9893] mb-1.5">
                      <span>Revision allowance</span>
                      <span>{design.revisionsUsed ?? 0} of {design.revisionAllowance ?? 0} used</span>
                    </div>
                    <div className="h-1.5 bg-[#F4F3F0] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 100 ? 'bg-[#C0222B]' : pct >= 66 ? 'bg-[#92560C]' : 'bg-[#3D52A0]'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
              </div>

              {versions.length > 0 && (
                <div className="border-t border-[#F4F3F0] px-5 py-4 flex items-center gap-3 flex-wrap">
                  {versions.map((v, i) => (
                    <div
                      key={v.id}
                      onClick={() => { setViewerIndex(i); setViewer(design); }}
                      className={`relative rounded-lg overflow-hidden cursor-pointer ring-2 ring-offset-1 ${v.versionNumber === design.currentVersion ? 'ring-[#3D52A0]' : 'ring-transparent hover:ring-[#C9C7C2]'}`}
                    >
                      {v.frontImage ? <img src={v.frontImage} alt={`V${v.versionNumber}`} className="w-20 h-24 object-cover" /> : <div className="w-20 h-24 bg-[#F4F3F0]" />}
                      <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] font-semibold text-center py-1">
                        V{v.versionNumber}
                        {v.status === VERSION.REVISION && <span className="ml-1 text-[#F7C97A]">↩</span>}
                        {v.status === VERSION.PENDING && <span className="ml-1 text-[#6B7FC4]">●</span>}
                      </div>
                    </div>
                  ))}
                  {reviewable(design) && (
                    <div className="flex flex-col gap-1.5 ml-2">
                      <Button variant="success" size="xs" icon={<CheckCircle size={11} />} onClick={() => openModal(setApproveTarget, design)}>Approve V{design.currentVersion}</Button>
                      <Button variant="secondary" size="xs" icon={<RefreshCw size={11} />} onClick={() => openModal(setRevisionTarget, design)}>Request revision</Button>
                    </div>
                  )}
                  {design.status === STATUS.APPROVED && design.finalisationStatus !== FINALISED && (
                    <Button variant="primary" size="xs" icon={<Lock size={11} />} className="ml-2" onClick={() => openModal(setFinaliseTarget, design)}>Finalise</Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {viewer && viewerVersion && (
        <div className="fixed inset-0 z-50 bg-[#1A1915] flex">
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center gap-4 px-6 py-4 border-b border-white/10">
              <button onClick={() => setViewer(null)} className="text-white/60 hover:text-white"><ChevronLeft size={20} /></button>
              <div className="flex-1">
                <h2 className="text-white font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{viewer.name}</h2>
                <p className="text-white/60 text-xs">Version {viewerVersion.versionNumber} · {label(DESIGN_VERSION_STATUS, viewerVersion.status)}</p>
                {viewerVersion.revisionSummary && <p className="text-white/50 text-xs mt-0.5">{viewerVersion.revisionSummary}</p>}
              </div>
              {reviewable(viewer) && (
                <div className="flex gap-2">
                  <Button variant="success" size="sm" icon={<CheckCircle size={13} />} onClick={() => openModal(setApproveTarget, viewer)}>Approve</Button>
                  <Button variant="secondary" size="sm" icon={<RefreshCw size={13} />} onClick={() => openModal(setRevisionTarget, viewer)}>Revision</Button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto flex items-start justify-center gap-4 p-8">
              {[['Front', viewerVersion.frontImage], ['Back', viewerVersion.backImage]].map(([side, src]) => (
                <div key={side} className="space-y-2">
                  {src ? <img src={src} alt={side} className="h-[520px] w-auto object-contain rounded-lg shadow-xl" /> : <div className="h-[520px] w-80 bg-white/5 rounded-lg" />}
                  <p className="text-white/40 text-xs text-center">{side}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 px-6 py-4 border-t border-white/10">
              <p className="text-white/60 text-xs">Versions:</p>
              {viewer.versions.map((v, i) => (
                <button key={v.id} onClick={() => setViewerIndex(i)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${i === viewerIndex ? 'bg-[#3D52A0] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
                  V{v.versionNumber}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Modal
        open={!!chargeTarget}
        onClose={() => setChargeTarget(null)}
        title="Review Design Charge"
        subtitle={chargeTarget?.name}
        size="md"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" icon={<XCircle size={14} />} onClick={rejectCharge}>Reject charge</Button>
            <Button variant="primary" icon={<CheckCircle size={14} />} onClick={approveCharge}>Approve charge</Button>
          </div>
        }
      >
        {chargeTarget && (
          <div className="space-y-4">
            <div className="bg-[#EEF0FB] border border-[#B8C1E8] rounded-xl p-4 text-xs text-[#3D52A0]">
              Approving authorises the manufacturer to begin design work. The charge is held against the engagement wallet.
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['Charge amount', formatINR(chargeTarget.charge)],
                ['Revisions included', chargeTarget.revisionAllowance ?? '—'],
                ['Garment type', label(GARMENT_TYPE, chargeTarget.garmentType)],
                ['Engagement', chargeEngagement?.name || '—'],
                ['Current available balance', chargeEngagement ? formatINR(chargeEngagement.availableBalance) : '—'],
                ['Projected available balance', chargeEngagement ? formatINR((chargeEngagement.availableBalance || 0) - (chargeTarget.charge || 0)) : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-[#F4F3F0]">
                  <span className="text-[#9B9893]">{k}</span>
                  <span className="font-semibold text-[#1A1915]">{v}</span>
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Note</label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={textareaCls} />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        title="Approve Design Version"
        subtitle={approveTarget ? `${approveTarget.name} — Version ${approveTarget.currentVersion}` : ''}
        size="sm"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button variant="success" icon={<CheckCircle size={14} />} onClick={approveVersion}>Approve version</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-[#5C5A54]">Approving marks this version acceptable. You can finalise the design afterwards.</p>
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Approval note</label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={textareaCls} />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!finaliseTarget}
        onClose={() => setFinaliseTarget(null)}
        title="Finalise Design"
        subtitle={finaliseTarget?.name}
        size="md"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setFinaliseTarget(null)}>Cancel</Button>
            <Button variant="primary" icon={<Lock size={14} />} disabled={!confirmed} onClick={finalise}>Finalise design</Button>
          </div>
        }
      >
        {finaliseTarget && (
          <div className="space-y-4">
            <div className="bg-[#FEF3E2] border border-[#F7C97A] rounded-xl p-4 flex items-start gap-2.5">
              <AlertTriangle size={14} className="text-[#92560C] mt-0.5 shrink-0" />
              <p className="text-xs text-[#92560C]">Finalising locks the selected version. Any future change requires a change request.</p>
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['Final version', `V${finaliseTarget.currentVersion}`],
                ['Linked fabric', finaliseTarget.linkedFabricOptionId ? `#${finaliseTarget.linkedFabricOptionId}` : '—'],
                ['Revisions used', `${finaliseTarget.revisionsUsed ?? 0} of ${finaliseTarget.revisionAllowance ?? 0}`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[#F4F3F0] py-1.5">
                  <span className="text-[#9B9893]">{k}</span>
                  <span className="font-semibold text-[#1A1915]">{v}</span>
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Finalisation note</label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={textareaCls} />
            </div>
            <label className="flex items-start gap-2 text-xs text-[#5C5A54]">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
              I confirm I have reviewed the final version, specifications and linked fabric, and authorise finalisation.
            </label>
          </div>
        )}
      </Modal>

      <Modal
        open={!!revisionTarget}
        onClose={() => setRevisionTarget(null)}
        title="Request Design Revision"
        subtitle={revisionTarget ? `${revisionTarget.name} — V${revisionTarget.currentVersion}` : ''}
        size="md"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setRevisionTarget(null)}>Cancel</Button>
            <Button variant="primary" icon={<RefreshCw size={14} />} disabled={!reason || !note.trim()} onClick={requestRevision}>Submit revision request</Button>
          </div>
        }
      >
        {revisionTarget && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-xs">
              <span className="text-[#9B9893]">Revisions remaining:</span>
              <span className={`font-bold ${(revisionTarget.revisionsUsed ?? 0) >= (revisionTarget.revisionAllowance ?? 0) ? 'text-[#C0222B]' : 'text-[#1A6B3E]'}`}>
                {Math.max(0, (revisionTarget.revisionAllowance ?? 0) - (revisionTarget.revisionsUsed ?? 0))} of {revisionTarget.revisionAllowance ?? 0}
              </span>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Revision reason *</label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                {REVISION_REASONS.map((r) => (
                  <label key={r} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-xs ${reason === r ? 'bg-[#EEF0FB] border-[#3D52A0] text-[#3D52A0] font-medium' : 'border-[#E5E3DE] text-[#5C5A54] hover:bg-[#F4F3F0]'}`}>
                    <input type="radio" name="rev-reason" value={r} checked={reason === r} onChange={() => setReason(r)} className="sr-only" />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Detailed feedback *</label>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={textareaCls} placeholder="Describe the specific changes needed…" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
